/**
 * Authentication and authorization constants
 * Single source of truth for user whitelist
 */

export type UserRole = 'SUPER_ADMIN' | 'ADMIN' | 'STAFF';

export interface WhitelistEntry {
  role: UserRole;
  name: string;
}

/**
 * Whitelist of allowed Telegram IDs
 * Only these users can access the Leafstock app
 *
 * To add a new user:
 * 1. Get their Telegram ID (they can find it using @userinfobot)
 * 2. Add an entry here with their ID, role, and name
 * 3. Redeploy both API and webapp
 */
export const ALLOWED_USERS: Record<string, WhitelistEntry> = {
  '7584507876': { role: 'SUPER_ADMIN', name: 'Mic' },      // Developer
  '1061753921': { role: 'ADMIN', name: 'Bryce' },          // Business Owner
  '8136523696': { role: 'STAFF', name: 'Staff Phone' },    // Boniswa/Joy/Bryce (shared phone)
  '8565478555': { role: 'STAFF', name: 'Joy Iphone' },    // Joy's personal phone
  '5763836543': { role: 'STAFF', name: 'Boniswa Phone' }, // Boniswa's personal phone
};

/**
 * Check if a Telegram ID is whitelisted
 */
export function isWhitelisted(telegramId: string | number): boolean {
  return String(telegramId) in ALLOWED_USERS;
}

/**
 * Get the whitelist entry for a Telegram ID
 * Returns undefined if not whitelisted
 */
export function getWhitelistEntry(telegramId: string | number): WhitelistEntry | undefined {
  return ALLOWED_USERS[String(telegramId)];
}

/**
 * Role hierarchy for permission checks
 * Higher index = more permissions
 */
export const ROLE_HIERARCHY: readonly UserRole[] = ['STAFF', 'ADMIN', 'SUPER_ADMIN'] as const;

/**
 * Check if a role has at least the required permission level
 */
export function hasRole(userRole: UserRole, requiredRole: UserRole): boolean {
  const userRoleIndex = ROLE_HIERARCHY.indexOf(userRole);
  const requiredRoleIndex = ROLE_HIERARCHY.indexOf(requiredRole);
  return userRoleIndex >= requiredRoleIndex;
}
